// Zweck: Google-Sitzung und begrenzten Login-Fallback zentral verwalten.
// Kontext: One Tap darf geschützte Routen nicht dauerhaft blockieren.
// Stand: 2026-09-10
// @refresh reset
import React, { createContext, useState, useEffect, useContext, useCallback, useRef, ReactNode } from 'react';
import { useGoogleOAuth } from '@react-oauth/google';
import { useNavigate, useLocation } from 'react-router-dom';
import { clearSyncState } from '../services/queueService';
import { clearDiagnosticLog } from '../services/diagnosticLog';
import { useDealsStore } from '../store/dealsStore';
import { useTemplateCacheStore } from '../store/templateCacheStore';
import { AuthUser } from '../types/auth';
import { fetchMeAsync } from '../services/api/auth';
import { ONE_TAP_GRACE_MS, AUTH_ME_RETRY_TIMEOUT_MS } from '../constants/auth';
import {
  setIdToken,
  persistAuthState,
  clearAuthState,
  getCachedProfile,
  getEmailFromToken,
  getIdToken,
  isTokenExpired,
} from '../services/googleAuthSession';

import type { AuthContextType } from '../interfaces/auth-context';
import { getGoogleIdentity } from '../interfaces/google-identity';
import { isItpBrowser } from '../utils/browser';

// Wie oft ein transienter (nicht auth-bezogener) Fehler automatisch und still erneut
// versucht wird, bevor der Nutzer auf den manuellen Login-Button zurückfällt.
const MAX_TRANSIENT_LOGIN_RETRIES = 1;
const TRANSIENT_LOGIN_RETRY_DELAY_MS = 800;

interface LoginRedirectState {
  from?: {
    pathname?: unknown;
    search?: unknown;
    hash?: unknown;
  };
}

interface LoginDestination {
  pathname: string;
  search: string;
  hash: string;
}

// Die Route stammt ausschließlich aus AuthGuard. Die Validierung verhindert dennoch,
// dass ein fremder oder unvollständiger Location-State als Navigationsziel verwendet wird.
function getLoginDestination(state: unknown): LoginDestination | null {
  const from = (state as LoginRedirectState | null)?.from;
  if (!from || typeof from.pathname !== 'string' || !from.pathname.startsWith('/') || from.pathname.startsWith('//')) {
    return null;
  }

  return {
    pathname: from.pathname,
    search: typeof from.search === 'string' ? from.search : '',
    hash: typeof from.hash === 'string' ? from.hash : '',
  };
}

// 401/403 sind echte Ablehnungen (ungültiges Token / falsches Konto) und werden bewusst
// NICHT automatisch wiederholt. 502/503/504 sowie fehlende Antwort (Netzwerk-/Timeout-
// Fehler ohne response) sind server-/netzwerkseitig und daher retry-fähig.
function isTransientLoginError(err: unknown): boolean {
  const e = err as { isAuthMissing?: boolean; status?: number } | null | undefined;
  if (e?.isAuthMissing) return false;
  const status = e?.status;
  return !status || status === 502 || status === 503 || status === 504;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  idToken: null,
  isAuthenticated: false,
  isLoading: true,
  isOneTapActive: true,
  isGoogleReady: false,
  isSlowLoginRetry: false,
  authError: null,
  loginWithGoogleJwt: async () => {},
  triggerSilentLogin: () => {},
  logout: async () => {},
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [idToken, setIdTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [authError, setAuthError] = useState<'wrong-account' | 'logged-out' | null>(null);
  const [oneTapEnabled, setOneTapEnabled] = useState(true);
  // Zeigt an, dass der automatische Retry mit dem längeren Timeout läuft (siehe
  // AUTH_ME_RETRY_TIMEOUT_MS) - erlaubt der UI, in dieser Zeit einen Hinweis
  // einzublenden, statt den Nutzer ohne Rückmeldung warten zu lassen.
  const [isSlowLoginRetry, setIsSlowLoginRetry] = useState(false);
  const { clientId, scriptLoadedSuccessfully } = useGoogleOAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Aktuelle Route per Ref statt als Closure-Dependency lesen: loginWithGoogleJwt kann
  // lange nach dem letzten Render aufgerufen werden (spät eintreffendes Credential,
  // Retry-Versuch) - über location.pathname als useCallback-Dependency hätte der Aufruf
  // sonst die Route vom Zeitpunkt der Callback-Erzeugung gesehen statt der aktuellen.
  const locationRef = useRef(location);
  useEffect(() => {
    locationRef.current = location;
  }, [location]);
  // Läuft der Credential-Austausch (fetchMeAsync) gerade, darf die feste Gnadenfrist
  // isLoading nicht vorzeitig auf false setzen - sonst leitet AuthGuard auf /login um,
  // obwohl der Login kurz darauf noch erfolgreich abschließt (nur per Refresh sichtbar
  // behoben, weil der dann persistierte Token synchron aus sessionStorage gelesen wird).
  // Dient außerdem als Serialisierung: überlappende Aufrufe (z.B. manueller Login-Button
  // und ein spät geliefertes One-Tap-Credential gleichzeitig) würden sich sonst gegenseitig
  // überschreiben - ein späterer 403 könnte einen bereits erfolgreichen Login wieder
  // zurücksetzen. Ein während eines laufenden Austauschs verworfener Aufruf kann vom
  // Nutzer einfach erneut ausgelöst werden (z.B. erneuter Klick).
  const isExchangingRef = useRef(false);
  // Zählt automatische Retries eines transienten Fehlers (siehe isTransientLoginError)
  // für den aktuellen Login-Versuch - wird bei jedem NEUEN (nicht selbst durch Retry
  // ausgelösten) Aufruf zurückgesetzt, damit ein späterer, unabhängiger Fehler wieder
  // sein eigenes Retry-Budget bekommt.
  const transientRetryCountRef = useRef(0);
  // Timer-Handle des geplanten stillen Retries - muss bei Logout/neuem Login-Versuch
  // explizit abgebrochen werden. Sonst könnte ein verspätet feuernder Retry für Token A
  // sich noch anmelden (oder eine zwischenzeitlich erfolgreiche Anmeldung mit Token B
  // überschreiben), obwohl der Nutzer sich längst ausgeloggt oder mit einem anderen
  // Konto neu angemeldet hat (siehe PR-Review-Kommentar zu #309).
  const retryTimeoutRef = useRef<number | null>(null);
  // Die One-Tap-Gnadenfrist gehört zu einem konkreten Prompt-Versuch. Sie muss
  // bei jedem erneuten Prompt neu starten, sonst kann der Spinner nach einem
  // Konto-Fehler dauerhaft aktiv bleiben.
  const oneTapGraceTimeoutRef = useRef<number | null>(null);
  // Erhöht sich bei jedem NEUEN (nicht durch den eigenen Retry-Timer ausgelösten)
  // Login-Versuch sowie bei Logout - ein Retry prüft vor der Ausführung, ob die
  // Generation noch dieselbe ist wie beim Planen, und bricht sonst ab.
  const loginGenerationRef = useRef(0);
  // Ein Logout muss sofort wirksam sein, auch wenn ein `online`-Event noch vor
  // dem nächsten React-Render eintrifft.
  const hasExplicitlyLoggedOutRef = useRef(false);
  const gisInitializedRef = useRef(false);
  const [isGoogleReady, setIsGoogleReady] = useState(false);

  const clearPendingLoginRetry = useCallback(() => {
    if (retryTimeoutRef.current !== null) {
      window.clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
  }, []);

  const clearOneTapGracePeriod = useCallback(() => {
    if (oneTapGraceTimeoutRef.current !== null) {
      window.clearTimeout(oneTapGraceTimeoutRef.current);
      oneTapGraceTimeoutRef.current = null;
    }
  }, []);

  const startOneTapGracePeriod = useCallback(() => {
    clearOneTapGracePeriod();
    // Auch ITP-Browser müssen den AuthGuard nach der Frist freigeben. Der
    // interaktive Google-Dialog darf dabei weiterlaufen und spät antworten.
    oneTapGraceTimeoutRef.current = window.setTimeout(() => {
      oneTapGraceTimeoutRef.current = null;
      if (!isExchangingRef.current) {
        // Außerhalb des interaktiven ITP-Flows den alten Prompt schließen.
        if (!isItpBrowser()) {
          getGoogleIdentity()?.cancel();
          setOneTapEnabled(false);
        }
        setIsLoading(false);
      }
    }, ONE_TAP_GRACE_MS);
  }, [clearOneTapGracePeriod]);

  // Timer beim Unmount der Provider-Instanz aufräumen (z.B. in Tests) - im laufenden
  // Betrieb lebt AuthProvider zwar für die gesamte App-Laufzeit, aber ohne dieses
  // Cleanup könnte ein Test-Setup/Teardown einen Timer versehentlich überleben lassen.
  useEffect(() => () => {
    clearPendingLoginRetry();
    clearOneTapGracePeriod();
  }, [clearOneTapGracePeriod, clearPendingLoginRetry]);

  const handleOneTapSkip = useCallback(() => {
    getGoogleIdentity()?.cancel();
    clearOneTapGracePeriod();
    setOneTapEnabled(false);
    if (!isExchangingRef.current) setIsLoading(false);
  }, [clearOneTapGracePeriod]);

  const promptOneTap = useCallback(() => {
    if (hasExplicitlyLoggedOutRef.current) return;
    getGoogleIdentity()?.prompt((notification) => {
      // FedCM liefert keine Display- und detaillierten Skip-Momente. Ein
      // generischer Skip-Moment bleibt aber teilweise verfügbar und erlaubt den
      // schnellen Fallback, wenn Google keinen Kandidaten liefern kann.
      if (notification.isSkippedMoment?.()) {
        console.warn('[Auth] One Tap übersprungen');
        handleOneTapSkip();
      } else if (notification.isDismissedMoment?.() && notification.getDismissedReason?.() !== 'credential_returned') {
        console.warn('[Auth] One Tap abgebrochen:', notification.getDismissedReason?.());
        handleOneTapSkip();
      }
    });
  }, [handleOneTapSkip]);

  const triggerSilentLogin = useCallback(() => {
    if (hasExplicitlyLoggedOutRef.current) return;
    setOneTapEnabled(true);
    setIsLoading(true);
    startOneTapGracePeriod();
    promptOneTap();
  }, [promptOneTap, startOneTapGracePeriod]);

  const loginWithGoogleJwt = useCallback(async (
    token: string,
    isRetryAttempt = false,
    retryGeneration?: number
  ): Promise<void> => {
    if (isExchangingRef.current) {
      console.warn('[Auth] loginWithGoogleJwt: Austausch läuft bereits, Aufruf verworfen');
      return;
    }
    if (!isRetryAttempt) {
      // Neuer, eigenständiger Versuch (manueller Klick, frisches One-Tap-Credential) -
      // ein noch ausstehender Retry eines VORHERIGEN Versuchs ist damit obsolet und darf
      // diesen neuen Versuch nicht mehr überschreiben können.
      clearPendingLoginRetry();
      loginGenerationRef.current += 1;
      transientRetryCountRef.current = 0;
    } else if (retryGeneration !== loginGenerationRef.current) {
      // Zwischenzeitlich hat sich die Session geändert (Logout, neuer Login-Versuch
      // mit einem anderen Token) - dieser verspätete Retry ist nicht mehr gültig.
      return;
    }
    const myGeneration = loginGenerationRef.current;
    clearOneTapGracePeriod();
    isExchangingRef.current = true;
    // Reaktiviert den Ladezustand auch dann, wenn die feste Gnadenfrist schon
    // abgelaufen war (Credential kam spät) und AuthGuard zwischenzeitlich bereits auf
    // /login umgeleitet hat - der Redirect selbst kann dadurch nicht mehr verhindert
    // werden, aber der anschließende Erfolg zeigt wieder den Ladezustand statt eines
    // Flackerns und navigiert danach zuverlässig zurück auf /projects.
    setIsLoading(true);
    // Bei einem transienten Fehler (503 etc.) läuft ein stiller automatischer
    // Retry mit demselben Token (siehe unten) -
    // isLoading darf dafür NICHT im finally-Block auf false fallen, sonst rendert
    // AuthGuard zwischenzeitlich unauthentifiziert.
    let awaitingRetry = false;
    let didFail = false;
    try {
      // Retry bekommt ein längeres Timeout als der Erstversuch: nach längerer
      // Inaktivität braucht der Server (One360-DEV-API-Cold-Start) messbar länger
      // als die für den Erstversuch gewählten 8s, ist aber nicht wirklich gehängt
      // - siehe AUTH_ME_RETRY_TIMEOUT_MS.
      if (isRetryAttempt) setIsSlowLoginRetry(true);
      const profile = isRetryAttempt
        ? await fetchMeAsync(token, AUTH_ME_RETRY_TIMEOUT_MS)
        : await fetchMeAsync(token);
      if (loginGenerationRef.current !== myGeneration) {
        // Der Request selbst wurde nicht abgebrochen (fetchMeAsync liefert weiterhin eine
        // Antwort), aber währenddessen hat sich die Session geändert (Logout, auth:unauthorized,
        // neuer Login-Versuch) - die Generation wurde dabei erhöht. Ein jetzt noch angewendeter
        // Erfolg würde z.B. einen zwischenzeitlichen Logout rückgängig machen (Token/User
        // setzen, Session persistieren, nach /projects navigieren), obwohl der Nutzer sich
        // bereits abgemeldet hat - besonders relevant seit der Retry bis zu 45s laufen kann
        // (PR-Review zu #318).
        console.warn('[Auth] loginWithGoogleJwt: Erfolgreiche Antwort nach Generationswechsel verworfen (Logout/neuer Versuch währenddessen)');
        return;
      }
      setIdTokenState(token);
      setIdToken(token);
      setUser(profile);
      setAuthError(null);
      hasExplicitlyLoggedOutRef.current = false;
      // PR-Review-Finding P1 (#384): auth:wrong-account wird nur bei einem serverseitig
      // erkannten 403 ausgelöst - ein stiller Kontowechsel per One Tap nach Browser-
      // Neustart (Token in sessionStorage, dieser Cache in localStorage) durchläuft diesen
      // Pfad nicht, obwohl beide Konten für sich genommen berechtigt sind. Deshalb hier
      // zusätzlich die zuletzt persistierte Profil-E-Mail mit der neuen vergleichen.
      const previousProfileEmail = getCachedProfile()?.email;
      if (previousProfileEmail && previousProfileEmail !== profile.email) {
        useTemplateCacheStore.getState().clearAll();
        // Re-Review-Finding (#424/#425): derselbe Cross-User-Grund wie beim
        // useTemplateCacheStore-Reset direkt darüber - ohne diesen Aufruf bliebe das
        // geräteweite Diagnose-Log des vorherigen Kontos (dealName, Projekt-/Phasen-IDs)
        // für das neue, ebenfalls berechtigte Konto weiterhin über den Diagnose-Export
        // einsehbar.
        clearDiagnosticLog();
      }
      persistAuthState(profile);
      const currentLocation = locationRef.current;
      if (currentLocation.pathname === '/login') {
        navigate(getLoginDestination(currentLocation.state) ?? '/projects', { replace: true });
      } else if (currentLocation.pathname === '/') {
        navigate('/projects', { replace: true });
      }
    } catch (err: unknown) {
      didFail = true;
      console.error('[Auth] loginWithGoogleJwt fehlgeschlagen:', err);
      const loginError = err as { status?: number } | null | undefined;
      if (loginError?.status === 403) {
        window.dispatchEvent(new CustomEvent('auth:wrong-account'));
      } else if (isTransientLoginError(err) && transientRetryCountRef.current < MAX_TRANSIENT_LOGIN_RETRIES) {
        // Server-/netzwerkseitiger Hänger (z.B. der jetzt auf 8-10s begrenzte Backend-
        // Timeout) - beim One Tap soll der Nutzer davon nichts merken, daher still und
        // automatisch mit demselben (noch gültigen) Google-ID-Token erneut versuchen,
        // statt ihn auf den manuellen Login-Button zurückzuwerfen.
        awaitingRetry = true;
        transientRetryCountRef.current += 1;
        retryTimeoutRef.current = window.setTimeout(() => {
          retryTimeoutRef.current = null;
          void loginWithGoogleJwt(token, true, myGeneration);
        }, TRANSIENT_LOGIN_RETRY_DELAY_MS);
      }
    } finally {
      isExchangingRef.current = false;
      if (isRetryAttempt) setIsSlowLoginRetry(false);
      if (!awaitingRetry) {
        // Nach einem endgültig fehlgeschlagenen Austausch wurde die One-Tap-
        // Gnadenfrist bereits beim Credential-Eingang beendet. Ohne dieses
        // Deaktivieren bliebe der manuelle Fallback verborgen.
        if (didFail) setOneTapEnabled(false);
        setIsLoading(false);
      }
    }
  }, [clearOneTapGracePeriod, clearPendingLoginRetry, navigate]);

  useEffect(() => {
    // Eine laufende Browser-Session darf nach einem Reload weiterverwendet werden.
    // Der Token wird dabei nicht blind vertraut: fetchMeAsync prüft ihn serverseitig,
    // bevor user/isAuthenticated gesetzt wird. So entfällt der unnötige Google-Login,
    // während abgelaufene oder abgelehnte Tokens weiterhin sicher verworfen werden.
    const existingToken = getIdToken();
    if (existingToken && !isTokenExpired(existingToken)) {
      setIdTokenState(existingToken);
      setOneTapEnabled(false);
      void loginWithGoogleJwt(existingToken);
      return clearOneTapGracePeriod;
    }

    // Ungültige oder abgelaufene Tokens dürfen nicht durch API-Interceptor weiter
    // verwendet werden, während die sichtbare Sitzung bereits abgemeldet ist.
    setIdToken(null);
    startOneTapGracePeriod();
    return clearOneTapGracePeriod;
  }, [clearOneTapGracePeriod, loginWithGoogleJwt, startOneTapGracePeriod]);

  useEffect(() => {
    const googleIdentity = getGoogleIdentity();
    if (!scriptLoadedSuccessfully || !googleIdentity || gisInitializedRef.current) return;

    // Google schreibt vor, diese Instanz nur einmal pro Seite zu initialisieren.
    // Anschließend verwenden sowohl One Tap (`prompt`) als auch der Button
    // (`renderButton`) exakt dieselbe Callback-Konfiguration.
    googleIdentity.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response.credential) void loginWithGoogleJwt(response.credential);
      },
      auto_select: true,
      use_fedcm_for_prompt: true,
      use_fedcm_for_button: true,
      // Ohne itp_support versucht GIS in Safari/iOS/Firefox weiterhin den normalen One-Tap-Flow,
      // der wegen Safaris Intelligent Tracking Prevention nie ein Credential liefert und
      // nur über die eigene Gnadenfrist mit dismissed_reason "cancel_called" abbricht.
      // Mit itp_support: true schaltet Google auf einen ITP-kompatiblen Flow um
      // (Welcome-Page -> Popup statt Third-Party-Cookie-Check).
      itp_support: true,
    });
    gisInitializedRef.current = true;
    setIsGoogleReady(true);

    if (oneTapEnabled && !isExchangingRef.current && !hasExplicitlyLoggedOutRef.current && !authError) promptOneTap();
  }, [authError, clientId, loginWithGoogleJwt, oneTapEnabled, promptOneTap, scriptLoadedSuccessfully]);

  useEffect(() => {
    const handleWrongAccount = () => {
      const email = getEmailFromToken(idToken);
      if (email) getGoogleIdentity()?.revoke?.(email, () => {});
      setAuthError('wrong-account');
      setIdTokenState(null);
      setIdToken(null);
      // Session ungültig geworden - ein noch ausstehender Retry-Timer eines vorherigen
      // Versuchs darf jetzt nicht mehr feuern (siehe clearPendingLoginRetry-Doku).
      clearPendingLoginRetry();
      loginGenerationRef.current += 1;
      // Deals-Cache gehört ggf. einem anderen (vorherigen) Konto - der Store
      // persistiert unabhängig vom Auth-Zustand in localStorage, ohne diesen Reset
      // blieben fremde Deals für das falsche Konto sichtbar (siehe clearDeals-Doku).
      useDealsStore.getState().clearDeals();
      // Security-Review-Finding P1: derselbe Reset wie für useDealsStore, da dieser Cache
      // ebenfalls account-unabhängig in localStorage persistiert (s. templateCacheStore-Doku).
      useTemplateCacheStore.getState().clearAll();
      // #1231-Re-Review-Finding: derselbe Grund wie beim clearDiagnosticLog-Aufruf in
      // handleUnauthorized weiter unten - dieser Kontowechselpfad (Ablehnung des falschen
      // Google-Kontos) resettet Deals-/Template-Cache bereits aus genau diesem Cross-User-Grund,
      // ließ den geräteweiten Diagnose-Log-Ringpuffer bisher aber unangetastet. Ohne diesen
      // Aufruf bliebe der Log der vorherigen Sitzung für den anschließend über den manuellen
      // Google-Button angemeldeten anderen Nutzer weiterhin exportierbar.
      clearDiagnosticLog();
      // Nach einer konkreten Ablehnung ist der manuelle Google-Button der
      // verlässlichere Konto-Wechsler. Einen ggf. sichtbaren One-Tap-Dialog schließen,
      // damit beide Wege nicht parallel um dasselbe Konto konkurrieren.
      clearOneTapGracePeriod();
      getGoogleIdentity()?.cancel();
      setOneTapEnabled(false);
      setIsLoading(false);
    };
    const handleUnauthorized = () => {
      clearAuthState();
      setUser(null);
      setIdTokenState(null);
      setIdToken(null);
      useDealsStore.getState().clearDeals();
      // Security-Review-Finding P1: derselbe Reset wie für useDealsStore, da dieser Cache
      // ebenfalls account-unabhängig in localStorage persistiert (s. templateCacheStore-Doku).
      useTemplateCacheStore.getState().clearAll();
      // #1231-Review-Finding D: derselbe Grund wie oben - das Diagnose-Log ist ebenfalls
      // account-unabhängig in localStorage persistiert und könnte sonst Daten (dealName,
      // Projekt-/Phasen-IDs) aus der Sitzung des vorherigen Nutzers preisgeben.
      clearDiagnosticLog();
      clearPendingLoginRetry();
      loginGenerationRef.current += 1;
      // Ein abgelaufener Token mitten in der Session (z.B. beim Phasenwechsel) soll
      // sich nach Möglichkeit selbst reparieren: erneut still per One Tap versuchen,
      // statt den Nutzer direkt auf die Login-Seite mit manuellem Button zu werfen.
      // AuthGuard zeigt währenddessen den Spinner (isLoading=true), nicht /login -
      // schlägt der stille Versuch fehl, übernimmt startOneTapGracePeriod denselben
      // manuellen Fallback wie beim initialen Laden.
      triggerSilentLogin();
    };
    const handleOnline = () => {
      if (!hasExplicitlyLoggedOutRef.current && !authError) triggerSilentLogin();
    };

    window.addEventListener('auth:wrong-account', handleWrongAccount);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('auth:wrong-account', handleWrongAccount);
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
      window.removeEventListener('online', handleOnline);
    };
  }, [authError, clearOneTapGracePeriod, clearPendingLoginRetry, idToken, triggerSilentLogin]);

  const logout = (): void => {
    hasExplicitlyLoggedOutRef.current = true;
    setIdTokenState(null);
    setIdToken(null);
    setUser(null);
    setAuthError('logged-out');
    getGoogleIdentity()?.cancel();
    setOneTapEnabled(false);
    clearOneTapGracePeriod();
    clearAuthState();
    clearSyncState();
    useDealsStore.getState().clearDeals();
    // Security-Review-Finding P1: derselbe Reset wie für useDealsStore, da dieser Cache
    // ebenfalls account-unabhängig in localStorage persistiert (s. templateCacheStore-Doku).
    useTemplateCacheStore.getState().clearAll();
    // #1231-Review-Finding D: derselbe Grund wie oben - das Diagnose-Log ist ebenfalls
    // account-unabhängig in localStorage persistiert und könnte sonst Daten (dealName,
    // Projekt-/Phasen-IDs) aus der Sitzung des vorherigen Nutzers preisgeben.
    clearDiagnosticLog();
    // Ein noch ausstehender stiller Retry (z.B. für den gerade abgemeldeten Token) darf
    // nach dem Logout nicht mehr feuern und diesen wieder anmelden.
    clearPendingLoginRetry();
    loginGenerationRef.current += 1;
    navigate('/login');
  };

  const contextValue: AuthContextType = {
    user,
    idToken,
    isAuthenticated: !!user,
    isLoading,
    isOneTapActive: oneTapEnabled,
    isGoogleReady,
    isSlowLoginRetry,
    authError,
    loginWithGoogleJwt,
    triggerSilentLogin,
    logout,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
